import React, { useState } from 'react';
import { Card, Row, Col, DatePicker, Button, Statistic, Table, Space, ConfigProvider, theme } from 'antd';
import { FileExcelOutlined, PrinterOutlined, SearchOutlined, BarChartOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useAuthStore from '../../store/useAuthStore';
import { getStockSummary } from '../../api/reports.api';
import { formatQty, exportToCSV } from '../../utils/formatters';
import StockBadge from '../../components/common/StockBadge';

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-stock-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-stock-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }

    .light-stock-page .ant-picker-sm,
    .light-stock-page .ant-btn-sm {
      border-radius: 4px;
      font-size: 12px;
    }

    /* Table Header - Not pure white */
    .light-stock-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-stock-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    .light-stock-page .ant-statistic-title { font-size: 11px; font-weight: 600; text-transform: uppercase; margin-bottom: 4px; color: var(--text-3); }
    .light-stock-page .ant-statistic-content { font-size: 18px; font-weight: 700; }

    /* Print styling to hide controls */
    @media print {
      .no-print { display: none !important; }
      .light-stock-page { padding: 0 !important; }
      .light-stock-page .ant-card { border: none !important; box-shadow: none !important; }
    }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' },
  statBox: { borderRight: '1px solid var(--border)', padding: '0 16px' }
};

const StockSummaryPage = () => {
  const { user } = useAuthStore();
  const [asOfDate, setAsOfDate] = useState(dayjs());
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['stockSummary', user.plantID, asOfDate.format('YYYY-MM-DD')],
    queryFn: async () => {
      const res = await getStockSummary(user.plantID, { date: asOfDate.format('YYYY-MM-DD') });
      if (res && Array.isArray(res.items)) return res;
      if (Array.isArray(res)) return { items: res };
      return { items: [] };
    },
    enabled: !!user.plantID
  });

  const columns = [
    { title: '#', render: (t, r, i) => <span style={{ color: 'var(--text-3)' }}>{i + 1}</span>, width: 40 },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { title: 'HSN', dataIndex: 'hsnCode', key: 'hsnCode', render: v => <span style={{ color: 'var(--text-3)' }}>{v || '-'}</span> },
    { title: 'Opening', dataIndex: 'openingQty', render: v => formatQty(v) },
    { title: 'Purchased', dataIndex: 'purchaseQty', render: v => formatQty(v) },
    { title: 'Consumed', dataIndex: 'consumptionQty', render: v => formatQty(v) },
    { 
      title: 'Balance', 
      dataIndex: 'balanceQty', 
      render: (v, record) => {
        const consumptionBalance = v * (record.mFactor || 1);
        const color = consumptionBalance <= 0 ? '#ef4444' : consumptionBalance < 50 ? '#f59e0b' : '#10b981';
        return <span style={{ color, fontWeight: 700, fontSize: 14 }}>{formatQty(consumptionBalance)}</span>;
      }
    },
    { title: 'Status', render: (_, r) => <StockBadge qty={r.balanceQty * (r.mFactor || 1)} />, width: 100 }
  ];

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-stock-page" style={{ padding: '8px 16px' }}>
        
        <Card
          className="report-card"
          // Card Header with slightly tinted background (#f8fafc) to avoid pure white
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChartOutlined style={{ color: 'var(--text-3)' }} />
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)' }}>
                Stock Summary Report
              </span>
            </div>
          }
          extra={
            <Space className="no-print">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
              <Button size="small" type="primary" style={{ background: '#10b981' }} icon={<FileExcelOutlined />} onClick={() => exportToCSV(data?.items || [], columns, `StockSummary_${asOfDate.format('DD-MM-YYYY')}`)}>
                Export CSV
              </Button>
            </Space>
          }
        >
          {/* CONTROLS SECTION */}
          <div className="compact-section no-print" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>AS OF DATE :</span>
              <DatePicker size="small" value={asOfDate} onChange={setAsOfDate} format="DD MMM YYYY" allowClear={false} />
            </div>
            <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => refetch()} style={{ background: '#0f172a' }}>
              Load Report
            </Button>
          </div>

          {/* STATISTICS SECTION */}
          <div className="compact-section" style={{ padding: '12px 0', display: 'flex', justifyContent: 'space-around', background: '#fff' }}>
            <div style={{ ...S.statBox, flex: 1 }}>
              <Statistic title="Total Items" value={data?.totalItems || (data?.items?.length || 0)} valueStyle={{ color: 'var(--text-1)' }} />
            </div>
            <div style={{ ...S.statBox, flex: 1 }}>
              <Statistic title="Good Stock" value={data?.goodCount || '-'} valueStyle={{ color: '#10b981' }} />
            </div>
            <div style={{ ...S.statBox, flex: 1 }}>
              <Statistic title="Low Stock" value={data?.lowCount || '-'} valueStyle={{ color: '#f59e0b' }} />
            </div>
            <div style={{ padding: '0 16px', flex: 1 }}>
              <Statistic title="Critical Stock" value={data?.criticalCount || '-'} valueStyle={{ color: '#ef4444' }} />
            </div>
          </div>

          {/* DATA TABLE */}
          <div style={S.tableWrap}>
            <Table 
              dataSource={data?.items || []} 
              columns={columns} 
              loading={isLoading} 
              pagination={false} 
              rowKey="rawID" 
              size="small"
              scroll={{ x: 800 }}
              locale={{ emptyText: "No stock data available for the selected date." }}
            />
          </div>

        </Card>

      </div>
    </ConfigProvider>
  );
};

export default StockSummaryPage;